/**
 * WebSocket client with auto-reconnect and event subscription.
 *
 * Friday uses a per-workspace DO at `wss://<host>/ws/:wsId?t=<jwt>&u=<userId>`.
 * On connect, we send `subscribe` events for each `kind:id` the caller asked
 * to listen to. The DO filters broadcasts to matching subscribers.
 *
 * Usage:
 *   useRealtime({ wsId, onEvent })
 * where onEvent is called for every RealtimeEvent the WS delivers.
 */

import { useEffect, useRef } from 'react'
import type { RealtimeEvent, ClientEvent } from '@friday/shared/realtime'
import { getAccessToken } from './auth'
import { useAuth } from '@/features/auth/authContext'

interface UseRealtimeOpts {
  wsId: string | undefined
  /** When subscribed, send `subscribe` events to the DO. */
  subscribe?: Array<{ kind: 'project' | 'task' | 'workspace' | 'page'; id: string }>
  onEvent: (e: RealtimeEvent) => void
  onPresence?: (users: Array<{ userId: string; lastSeenAt: string }>) => void
}

export function useRealtime({ wsId, subscribe, onEvent, onPresence }: UseRealtimeOpts) {
  const wsRef = useRef<WebSocket | null>(null)
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const backoffRef = useRef(1000)
  const deadRef = useRef(false)
  const onEventRef = useRef(onEvent)
  const onPresenceRef = useRef(onPresence)
  const subscribeRef = useRef(subscribe)
  onEventRef.current = onEvent
  onPresenceRef.current = onPresence
  subscribeRef.current = subscribe

  const user = useAuth().user

  useEffect(() => {
    if (!wsId) return
    deadRef.current = false

    function connect() {
      if (deadRef.current) return
      const access = getAccessToken()
      if (!user) {
        // Try again shortly — auth may not be hydrated yet.
        setTimeout(connect, 1000)
        return
      }
      const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
      const url = `${proto}//${location.host}/ws/${encodeURIComponent(wsId!)}${access ? `?t=${encodeURIComponent(access)}&u=${encodeURIComponent(user.id)}` : ''}`
      let ws: WebSocket
      try {
        ws = new WebSocket(url)
      } catch {
        scheduleReconnect()
        return
      }
      wsRef.current = ws
      ws.addEventListener('open', () => {
        backoffRef.current = 1000
        for (const s of subscribeRef.current ?? []) {
          ws.send(JSON.stringify({ t: 'subscribe', kind: s.kind, id: s.id } satisfies ClientEvent))
        }
        if (heartbeatRef.current) clearInterval(heartbeatRef.current)
        heartbeatRef.current = setInterval(() => {
          try {
            ws.send(JSON.stringify({ t: 'heartbeat', clientTime: new Date().toISOString() } satisfies ClientEvent))
          } catch {}
        }, 30_000)
      })
      ws.addEventListener('message', (msg) => {
        let event: RealtimeEvent
        try {
          event = JSON.parse(msg.data as string) as RealtimeEvent
        } catch {
          return
        }
        if (event.t === 'presence' && onPresenceRef.current) {
          onPresenceRef.current(event.users)
          return
        }
        onEventRef.current(event)
      })
      ws.addEventListener('close', () => {
        if (heartbeatRef.current) clearInterval(heartbeatRef.current)
        scheduleReconnect()
      })
      ws.addEventListener('error', () => {
        try { ws.close() } catch {}
      })
    }

    function scheduleReconnect() {
      if (deadRef.current) return
      const delay = backoffRef.current
      backoffRef.current = Math.min(30_000, Math.floor(backoffRef.current * 1.7))
      setTimeout(connect, delay)
    }

    connect()
    return () => {
      deadRef.current = true
      if (heartbeatRef.current) clearInterval(heartbeatRef.current)
      const ws = wsRef.current
      if (ws) {
        try { ws.close() } catch {}
      }
    }
  }, [wsId, user])
}
